import { useEffect, useState } from 'react';
import { getPrismSyncState, PrismSyncState } from '../api/assets';

// prism 同步落库的读路径开关 (2026-10-20). readLocal=true 后:
//   - 材料加工并入历史加工数据 (数据来源分段 + 新筛选/新统计卡);
//   - 菜单隐藏「历史加工数据」, 其列表路由跳到材料加工的历史来源.
// 菜单与三个页面都要读它, 模块级缓存一次请求的结果, 别每个组件各发一遍.
// 状态条 (PrismSyncBar) 拿到新状态时调 setPrismStateCache 刷新这份缓存.

let cached: PrismSyncState | null = null;
let inflight: Promise<PrismSyncState | null> | null = null;
const listeners = new Set<(s: PrismSyncState | null) => void>();

export function loadPrismState(force = false): Promise<PrismSyncState | null> {
    if (cached && !force) return Promise.resolve(cached);
    if (!inflight) {
        inflight = getPrismSyncState()
            .then((s) => {
                setPrismStateCache(s);
                return s;
            })
            .catch(() => null) // 拿不到 (老后端 / 无权限) 按未切换处理, 页面保持原样
            .finally(() => {
                inflight = null;
            });
    }
    return inflight;
}

export function setPrismStateCache(s: PrismSyncState | null) {
    cached = s;
    listeners.forEach((fn) => fn(s));
}

/**
 * 当前同步状态 (首次挂载拉一次, 之后共享缓存).
 * enabled=false 时不发请求 (菜单栏对没有数据资产权限的人不该去打这个接口).
 */
export function usePrismSyncState(enabled = true): PrismSyncState | null {
    const [state, setState] = useState<PrismSyncState | null>(cached);
    useEffect(() => {
        if (!enabled) return undefined;
        listeners.add(setState);
        loadPrismState();
        return () => {
            listeners.delete(setState);
        };
    }, [enabled]);
    return enabled ? state : null;
}

/** 页面是否已改读本库 (未拿到状态前按 false, 页面行为与改造前一致). */
export function usePrismReadLocal(enabled = true): boolean {
    return usePrismSyncState(enabled)?.readLocal === true;
}
